import Send16 from '@carbon/icons-react/es/send/16';
import { pick } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { PermissionSetType, ProjectId, UserType } from 'api/models';
import { StatusUpdateState } from 'api/status-updates';
import { Button } from 'components/atoms/button';
import { Modal } from 'components/atoms/modal';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { toastService } from 'components/molecules/toast';
import { getFormattedUsersList } from 'components/organisms/project-invitations';
import { useProjectDetail } from 'data/projects';
import { createAndInviteProjectMembersAction } from 'data/projects/actions';
import {
  putStatusUpdates,
  selectActiveStatusUpdate,
  setActiveStatusUpdate,
  updateStatusUpdate,
} from 'data/status-updates';
import { useGetAllMembersByCompany } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  getStatusUpdatesDetailQueryKey,
  usePublishStatusUpdateMutation,
} from 'queries/status-updates';
// eslint-disable-next-line import/no-internal-modules
import { StatusUpdateFilters } from '../list/status-update-tabs';
import { ButtonWrapper, EmailSelect, Label, ModalTitle } from './styles';

interface EmailValues {
  label: string;
  value: string;
}

export const StatusUpdatePublishModal: FC<{
  onBeforePublish?: () => Promise<void>;
  onAsyncOperationSuccess: (info: { title: string; message: string }) => void;
  userEmails: EmailValues[];
  setUserEmails: (userEmails) => void;
  onTabChange: (tab) => void;
  hasAccessToPublish: boolean;
  projectId: number;
}> = ({
  onBeforePublish,
  onAsyncOperationSuccess,
  userEmails,
  setUserEmails,
  onTabChange,
  hasAccessToPublish,
  projectId: projectIdNumber,
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate)!;
  const [publishLoading, setPublishLoading] = useState(false);

  const [inviteAndPublishLoading, setInviteAndPublishLoading] = useState(false);

  const emails = useMemo(
    () =>
      activeStatusUpdate.state === StatusUpdateState.Published
        ? (userEmails ?? []).map((_mail) => ({ emailId: _mail.value }))
        : activeStatusUpdate.ccUsers,
    [activeStatusUpdate.ccUsers, activeStatusUpdate.state, userEmails],
  );
  const projectId = ProjectId(projectIdNumber);
  const { data: activeProject } = useProjectDetail(projectId);
  const dispatch = useDispatch();

  const currentUser = useUser();

  const { companyId } = currentUser.company;
  const customer = activeProject?.customer;
  const customerCompanyId = activeProject?.customer?.companyId;

  const vendorUserList = useGetAllMembersByCompany(companyId);
  const customerUserList = useGetAllMembersByCompany(customerCompanyId!);

  const publishedToastTitle = useTranslation(
    'status_updates.published_msg_title',
  );
  const publishedToastMessage = useTranslation(
    'status_updates.published_msg_desc',
  );

  const noUsersTitle = useTranslation('no.users.in.to.section');
  const noUsersBody = useTranslation('no.users.in.to.section.try.again');

  const publishModalTitle = useTranslation('su.invite.modal.title');
  const label = useTranslation('sc.invite.modal.input.lable');
  const sendText = useTranslation('su.cc.add_send');
  const publish = useTranslation('su.send.without.invite');
  const inviteAndPublish = useTranslation('su.invite.and.send');
  const ccUpdateToastTitle = useTranslation('su.cc.update_msg_title');
  const ccUpdateToastMessage = useTranslation('su.cc.update_msg_desc');

  const queryClient = useQueryClient();

  const filterList = useMemo(
    () =>
      currentUser.user.userType === UserType.Native
        ? [...vendorUserList, ...customerUserList]
        : customerUserList,
    [currentUser.user.userType, customerUserList, vendorUserList],
  );
  const filteredEmails = useMemo(
    () =>
      (emails ?? []).filter(
        (ccUser) => !filterList.some((user) => user.emailId === ccUser.emailId),
      ),
    [emails, filterList],
  );

  const emailOptions = useMemo(
    () =>
      filteredEmails.map((mail) => ({
        label: mail.emailId,
        value: mail.emailId,
      })),
    [filteredEmails],
  );

  const transformedOptions = useMemo(() => {
    const users = filteredEmails.map((cc) => ({
      label: cc.emailId,
      value: cc.emailId,
      company: { companyId: customerCompanyId! },
    }));

    const formattedUsersList = getFormattedUsersList(users);

    return formattedUsersList.map((u) => {
      const permissionSetType = PermissionSetType.CustomerChampion;

      return {
        user: {
          emailId: u.value,
          company: pick(customer!, 'companyId'),
        },
        permissionSet: { permissionSetType },
      };
    });
  }, [customer, customerCompanyId, filteredEmails]);

  const showPublishSuccessToast = () => {
    onAsyncOperationSuccess({
      title: publishedToastTitle,
      message: publishedToastMessage,
    });
  };

  const ccUpdateSuccessToast = () => {
    toastService({
      title: ccUpdateToastTitle,
      message: ccUpdateToastMessage,
    }).success();
  };

  const reset = () => {
    setPublishLoading(false);
    setInviteAndPublishLoading(false);
    setModalVisible(false);
    setUserEmails([]);
  };

  const onSuccess = (response) => {
    queryClient.invalidateQueries(
      getStatusUpdatesDetailQueryKey([
        'status_update_id',
        response.statusUpdateId,
      ]),
    );
    dispatch(updateStatusUpdate(response));
    dispatch(setActiveStatusUpdate(response));
    showPublishSuccessToast();
    onTabChange(StatusUpdateFilters.Sent);

    reset();
  };

  const ccUpdateSuccess = () => {
    ccUpdateSuccessToast();

    reset();
  };

  const { mutateAsync: publishStatusUpdateAsync, isLoading } =
    usePublishStatusUpdateMutation();

  const sendUpdate = async () => {
    if (
      onBeforePublish &&
      activeStatusUpdate.state !== StatusUpdateState.Published
    ) {
      await onBeforePublish();
    }

    if (activeStatusUpdate.state === StatusUpdateState.Draft) {
      publishStatusUpdateAsync(activeStatusUpdate.statusUpdateId, {
        onSuccess,
      });

      return;
    }

    const updatedStatusUpdate = {
      ...activeStatusUpdate,
      ccUsers: activeStatusUpdate.ccUsers.concat(
        (userEmails ?? []).map((_mail) => ({ emailId: _mail.value })),
      ),
    };

    dispatch(setActiveStatusUpdate(updatedStatusUpdate));
    dispatch(
      putStatusUpdates({
        statusUpdate: updatedStatusUpdate,
        ccUpdate: true,
        onSuccess: ccUpdateSuccess,
      }),
    );
  };

  const inviteAndPublishCc = () => {
    setInviteAndPublishLoading(true);
    if (transformedOptions.length) {
      dispatch(
        createAndInviteProjectMembersAction({
          projectId: projectId[1],
          data: transformedOptions,
          handleSuccess: sendUpdate,
        }),
      );
    }
  };

  const publishUpdate = () => {
    setPublishLoading(true);
    sendUpdate();
  };

  const onSend = () => {
    if (!activeStatusUpdate.toUsers?.length) {
      toastService({
        title: noUsersTitle,
        message: noUsersBody,
      }).error();

      return;
    }
    if (emailOptions.length) {
      setModalVisible(true);

      return;
    }
    sendUpdate();
  };

  const onCancel = () => {
    setModalVisible(false);
  };

  return (
    <>
      <Modal
        visible={isModalVisible}
        className="su-publish-modal"
        width={600}
        closable={false}
        footer={
          <ButtonWrapper>
            <Button onClick={onCancel} type="link">
              {useTranslation('cancel')}
            </Button>
            <div>
              <Button
                data-cy="status_update_no_invite_and_publish_button"
                loading={publishLoading}
                disabled={inviteAndPublishLoading}
                onClick={publishUpdate}
              >
                {publish}
              </Button>
              <Button
                data-cy="status_update_invite_and_publish_button"
                loading={inviteAndPublishLoading}
                onClick={inviteAndPublishCc}
                type="primary"
                disabled={publishLoading}
              >
                {inviteAndPublish}
              </Button>
            </div>
          </ButtonWrapper>
        }
      >
        <ModalTitle>{publishModalTitle}</ModalTitle>
        <Label>{label}</Label>
        <EmailSelect
          isDisabled
          showMenuOnOpen={false}
          isMulti
          type="user"
          isClearable
          options={[]}
          value={emailOptions}
        />
      </Modal>
      {hasAccessToPublish &&
      (activeStatusUpdate.state === StatusUpdateState.Draft ||
        emailOptions.length) ? (
        <TertiaryButton
          data-cy="status_update_publish_button"
          iconPlacement="right"
          icon={<Send16 />}
          loading={isLoading}
          onClick={onSend}
        >
          {sendText}
        </TertiaryButton>
      ) : null}
    </>
  );
};
